import { useEffect, useMemo, useRef, useState } from "preact/hooks";
import { errorMessage, putLoadout, selectLoadout } from "../../api/client";
import { EquipmentKind, SlotKind } from "../../api/types";
import type { FullLoadout } from "../../api/types";
import { requireMe, setMe } from "../../state/auth";
import { requireMeta, requireMetaIndex } from "../../state/meta";
import { goBack } from "../../state/route";
import { toast } from "../../state/toasts";
import { Badge } from "../components/Badge";
import { Button } from "../components/Button";
import { Field, TextInput } from "../components/Field";
import { Modal } from "../components/Modal";
import { ScreenFrame } from "../components/ScreenFrame";
import { Tabs, panelId, tabId } from "../components/Tabs";
import { useEscape } from "../hooks/hooks";
import { useArrowNav } from "../hooks/useArrowNav";
import {
  NAME_MAX,
  NAME_MIN,
  PERK_COUNT,
  chooseCamo,
  chooseEquipment,
  chooseWeapon,
  loadoutsEqual,
  toggleAttachment,
  togglePerk,
} from "./loadouts/draft";
import { EquipmentPicker, PerkPicker } from "./loadouts/GearPickers";
import { PreviewPanel } from "./loadouts/PreviewPanel";
import { Counter, Section } from "./loadouts/Section";
import { WeaponSection } from "./loadouts/WeaponSection";

const TABS_ID = "loadouts";

type Pending = { kind: "tab"; index: number } | { kind: "back" };
type Busy = "save" | "equip";

/**
 * Loadout editor: five slots, each edited as a local draft that is only sent on Save (or on Equip,
 * which saves first). `draft` is null while the slot is unchanged, so the working copy always follows
 * the account in `me` until the first edit.
 */
export function Loadouts() {
  const account = requireMe();
  const catalog = requireMeta();
  const index = requireMetaIndex();

  const [active, setActive] = useState(() =>
    Math.min(Math.max(account.selectedLoadout, 0), account.loadouts.length - 1),
  );
  const [draft, setDraft] = useState<FullLoadout | null>(null);
  const [pending, setPending] = useState<Pending | null>(null);
  const [busy, setBusy] = useState<Busy | null>(null);
  const [error, setError] = useState<string | null>(null);
  const alive = useRef(true);
  const root = useRef<HTMLDivElement>(null);
  useArrowNav(root);
  useEffect(() => {
    alive.current = true;
    return () => {
      alive.current = false;
    };
  }, []);

  const primaries = useMemo(() => catalog.weapons.filter((w) => w.slot === SlotKind.PRIMARY), [catalog]);
  const secondaries = useMemo(() => catalog.weapons.filter((w) => w.slot === SlotKind.SECONDARY), [catalog]);
  const lethals = useMemo(() => catalog.equipment.filter((e) => e.kind === EquipmentKind.LETHAL), [catalog]);
  const tacticals = useMemo(() => catalog.equipment.filter((e) => e.kind === EquipmentKind.TACTICAL), [catalog]);

  const saved = account.loadouts[active];
  const working = draft ?? saved;
  const dirty = draft !== null && !loadoutsEqual(draft, saved);
  const isEquipped = active === account.selectedLoadout;

  const nameLength = Array.from(working.name.trim()).length;
  const nameOk = nameLength >= NAME_MIN && nameLength <= NAME_MAX;
  let problem: string | null = null;
  if (!nameOk) problem = `Name needs ${NAME_MIN} to ${NAME_MAX} characters.`;
  else if (working.perks.length !== PERK_COUNT) problem = `Pick exactly ${PERK_COUNT} perks.`;

  const requestBack = () => {
    if (dirty) setPending({ kind: "back" });
    else goBack();
  };
  // While dirty, Escape asks before leaving; otherwise the global handler pops the screen.
  useEscape(requestBack, dirty);

  const edit = (change: (l: FullLoadout) => FullLoadout) => {
    const next = change(draft ?? structuredClone(saved));
    setDraft(loadoutsEqual(next, saved) ? null : next);
    setError(null);
  };

  const selectTab = (id: string) => {
    const next = Number(id);
    if (next === active || busy !== null) return;
    if (dirty) {
      setPending({ kind: "tab", index: next });
      return;
    }
    setActive(next);
    setError(null);
  };

  const discard = () => {
    const target = pending;
    setPending(null);
    setDraft(null);
    setError(null);
    if (target?.kind === "tab") setActive(target.index);
    else if (target?.kind === "back") goBack();
  };

  const revert = () => {
    setDraft(null);
    setError(null);
  };

  const run = async (kind: Busy, task: () => Promise<void>) => {
    if (busy !== null) return;
    setBusy(kind);
    setError(null);
    try {
      await task();
    } catch (e) {
      if (alive.current) setError(errorMessage(e));
    } finally {
      if (alive.current) setBusy(null);
    }
  };

  /** PUT the draft; the server answer replaces the account's loadouts. Edits made meanwhile are kept. */
  const persist = async (target: FullLoadout, at: number) => {
    const loadouts = await putLoadout(at, { ...target, name: target.name.trim() });
    setMe({ ...requireMe(), loadouts });
    if (alive.current) setDraft((current) => (current === target ? null : current));
  };

  const save = () =>
    run("save", async () => {
      await persist(working, active);
      toast("Loadout saved", "success");
    });

  const equip = () =>
    run("equip", async () => {
      if (dirty) await persist(working, active);
      await selectLoadout(active);
      setMe({ ...requireMe(), selectedLoadout: active });
      toast(`${working.name.trim()} equipped`, "success");
    });

  const tabs = account.loadouts.map((l, i) => ({
    id: String(i),
    label: (i === active ? working.name : l.name).trim() || `Loadout ${i + 1}`,
    mark: i === active && dirty ? "*" : undefined,
  }));

  let equipLabel = "Equip";
  if (isEquipped) equipLabel = "Equipped";
  else if (dirty) equipLabel = "Save & equip";

  return (
    <ScreenFrame
      title="Loadouts"
      kicker="Customize"
      onBack={requestBack}
      class="loadouts"
      bodyClass="loadouts__body"
      footer={
        <>
          <p class="loadouts__status" role="status" data-state={problem ? "error" : dirty ? "dirty" : "saved"}>
            {problem ?? (dirty ? "Unsaved changes" : "All changes saved")}
          </p>
          <div class="loadouts__actions">
            <Button variant="ghost" onClick={revert} disabled={!dirty || busy !== null}>
              Revert
            </Button>
            <Button
              class="loadouts__equip"
              onClick={equip}
              busy={busy === "equip"}
              disabled={isEquipped || busy !== null || (dirty && problem !== null)}
            >
              {equipLabel}
            </Button>
            <Button
              variant="primary"
              onClick={save}
              busy={busy === "save"}
              disabled={!dirty || problem !== null || busy !== null}
            >
              Save
            </Button>
          </div>
        </>
      }
    >
      <div ref={root} class="loadouts__root">
        <div class="loadouts__tabsbar" data-equipped={account.selectedLoadout}>
          <Tabs
            tabs={tabs}
            active={String(active)}
            onChange={selectTab}
            idPrefix={TABS_ID}
            ariaLabel="Loadout slots"
          />
        </div>
        <div
          class="loadouts__panel"
          role="tabpanel"
          id={panelId(TABS_ID, String(active))}
          aria-labelledby={tabId(TABS_ID, String(active))}
        >
          <div class="loadouts__main">
            <Section title="Loadout" aside={isEquipped && <Badge tone="ok">Equipped</Badge>}>
              <Field
                class="loadouts__name"
                label="Name"
                hint={`${NAME_MIN} to ${NAME_MAX} characters`}
                error={nameOk ? undefined : `Name needs ${NAME_MIN} to ${NAME_MAX} characters`}
              >
                {(id) => (
                  <TextInput
                    id={id}
                    value={working.name}
                    maxLength={NAME_MAX}
                    invalid={!nameOk}
                    autoComplete="off"
                    onValue={(name) => edit((l) => ({ ...l, name }))}
                  />
                )}
              </Field>
            </Section>
            <WeaponSection
              title="Primary weapon"
              weapons={primaries}
              camos={catalog.camos}
              choice={working.primary}
              account={account}
              index={index}
              onWeapon={(id) => edit((l) => chooseWeapon(l, SlotKind.PRIMARY, id))}
              onAttachment={(id) => edit((l) => toggleAttachment(l, SlotKind.PRIMARY, id, index.attachments))}
              onCamo={(id) => edit((l) => chooseCamo(l, SlotKind.PRIMARY, id))}
            />
            <WeaponSection
              title="Secondary weapon"
              weapons={secondaries}
              camos={catalog.camos}
              choice={working.secondary}
              account={account}
              index={index}
              onWeapon={(id) => edit((l) => chooseWeapon(l, SlotKind.SECONDARY, id))}
              onAttachment={(id) => edit((l) => toggleAttachment(l, SlotKind.SECONDARY, id, index.attachments))}
              onCamo={(id) => edit((l) => chooseCamo(l, SlotKind.SECONDARY, id))}
            />
            <Section title="Equipment">
              <h3 class="loadouts__sub">Lethal</h3>
              <EquipmentPicker
                label="Lethal equipment"
                items={lethals}
                selected={working.lethal}
                unlocked={account.unlocks.equipment}
                onSelect={(id) => edit((l) => chooseEquipment(l, EquipmentKind.LETHAL, id))}
              />
              <h3 class="loadouts__sub">Tactical</h3>
              <EquipmentPicker
                label="Tactical equipment"
                items={tacticals}
                selected={working.tactical}
                unlocked={account.unlocks.equipment}
                onSelect={(id) => edit((l) => chooseEquipment(l, EquipmentKind.TACTICAL, id))}
              />
            </Section>
            <Section title="Perks" aside={<Counter value={working.perks.length} max={PERK_COUNT} />}>
              <PerkPicker
                items={catalog.perks}
                selected={working.perks}
                unlocked={account.unlocks.perks}
                onToggle={(id) => edit((l) => togglePerk(l, id))}
              />
            </Section>
          </div>
          <aside class="loadouts__side" aria-label="Stat preview">
            <PreviewPanel loadout={working} index={index} />
          </aside>
        </div>
        {error !== null && (
          <div class="notice notice--error loadouts__notice" role="alert">
            <span class="loadouts__notice-text">{error}</span>
            <button type="button" class="loadouts__dismiss" onClick={() => setError(null)} data-nav="">
              Dismiss
            </button>
          </div>
        )}
      </div>
      {pending && (
        <Modal
          title="Discard changes?"
          size="sm"
          onClose={() => setPending(null)}
          footer={
            <>
              <Button variant="ghost" onClick={() => setPending(null)} data-autofocus="">
                Keep editing
              </Button>
              <Button variant="danger" onClick={discard}>
                Discard
              </Button>
            </>
          }
        >
          <p>
            “{working.name.trim() || `Loadout ${active + 1}`}” has unsaved changes.{" "}
            {pending.kind === "tab" ? "Switching to another slot" : "Leaving"} discards them.
          </p>
        </Modal>
      )}
    </ScreenFrame>
  );
}
